import { useState } from 'react';
import { ActionIcon, Menu, Popover, Tooltip } from '@mantine/core';
import {
    IconHandStop,
    IconLayoutGrid,
    IconLayoutSidebarRight,
    IconMoodSmile,
    IconPhone,
    IconScreenShare,
    IconScreenShareOff,
} from '@tabler/icons-react';
import { useRoomContext, useTrackToggle } from '@livekit/components-react';
import { type ScreenShareCaptureOptions, Track } from 'livekit-client';
import { useTranslation } from 'react-i18next';

import { ReactionPicker, useLocalHand, useSendReaction } from '../reactions';
import { useCallLayout } from './call-layout';
import classes from './control-bar.module.css';
import { LeaveDialog, useLeaveGuard } from './leave-dialog';
import { MediaToggle } from './media-toggle';
import { MoreMenu } from './more-menu';
import { RecordButton } from './record-button';
import { CALL_SHORTCUTS } from './shortcuts';
import { notifyError } from '@/shared/notify';
import { ShortcutHint } from '@/shared/shortcut-hint';

export const browserSupportsScreenSharing =
    'mediaDevices' in navigator && typeof navigator.mediaDevices.getDisplayMedia === 'function';

export const SCREEN_SHARE_CAPTURE_OPTIONS = { audio: true, selfBrowserSurface: 'include' } satisfies ScreenShareCaptureOptions;

export function ControlBar({
    onOpenSettings,
    onOpenShortcuts,
}: Readonly<{ onOpenSettings?: () => void; onOpenShortcuts: () => void }>) {
    const { t } = useTranslation('conference');
    const { t: tReactions } = useTranslation('reactions');
    const room = useRoomContext();
    const hand = useLocalHand();
    const sendReaction = useSendReaction();
    const [reactionsOpened, setReactionsOpened] = useState(false);
    const [leaveOpened, setLeaveOpened] = useState(false);
    const { layout, setLayout } = useCallLayout();
    const screenShare = useTrackToggle({
        source: Track.Source.ScreenShare,
        captureOptions: SCREEN_SHARE_CAPTURE_OPTIONS,
        onDeviceError: (error) => {
            notifyError(t('controls.screenShare.error'), error);
        },
    });

    const leaveGuard = useLeaveGuard(room);
    const closeLeave = () => {
        setLeaveOpened(false);
        if (leaveGuard.state === 'blocked') leaveGuard.reset();
    };

    const screenShareLabel = screenShare.enabled ? t('controls.screenShare.stop') : t('controls.screenShare.start');
    const handLabel = hand.raised ? tReactions('hand.lower') : tReactions('hand.raise');
    const tileLayout = layout === 'tile';
    const layoutLabel = tileLayout ? t('controls.layout.speaker') : t('controls.layout.tile');

    return (
        <div className={classes.bar}>
            <MediaToggle source={Track.Source.Microphone} />
            <MediaToggle source={Track.Source.Camera} />
            {browserSupportsScreenSharing && (
                <Tooltip label={<ShortcutHint keys={CALL_SHORTCUTS.screenShare} label={screenShareLabel} />}>
                    <ActionIcon
                        aria-label={screenShareLabel}
                        aria-pressed={screenShare.enabled}
                        color={screenShare.enabled ? 'action' : undefined}
                        disabled={screenShare.pending}
                        radius="var(--radius-rounded-full)"
                        size="lg"
                        variant={screenShare.enabled ? 'filled' : 'default'}
                        visibleFrom="sm"
                        onClick={() => void screenShare.toggle()}
                    >
                        {screenShare.enabled ? <IconScreenShareOff size={24} /> : <IconScreenShare size={24} />}
                    </ActionIcon>
                </Tooltip>
            )}
            <Tooltip label={<ShortcutHint keys={CALL_SHORTCUTS.layout} label={layoutLabel} />}>
                <ActionIcon
                    aria-label={t('controls.layout.tile')}
                    aria-pressed={tileLayout}
                    radius="var(--radius-rounded-full)"
                    size="lg"
                    variant="default"
                    visibleFrom="sm"
                    onClick={() => {
                        setLayout(tileLayout ? 'speaker' : 'tile');
                    }}
                >
                    {tileLayout ? <IconLayoutSidebarRight size={24} /> : <IconLayoutGrid size={24} />}
                </ActionIcon>
            </Tooltip>
            <Popover withinPortal opened={reactionsOpened} position="top" onChange={setReactionsOpened}>
                <Popover.Target>
                    <Tooltip disabled={reactionsOpened} label={tReactions('picker.label')}>
                        <ActionIcon
                            aria-expanded={reactionsOpened}
                            aria-label={tReactions('picker.label')}
                            radius="var(--radius-rounded-full)"
                            size="lg"
                            variant="default"
                            onClick={() => {
                                setReactionsOpened((opened) => !opened);
                            }}
                        >
                            <IconMoodSmile size={24} />
                        </ActionIcon>
                    </Tooltip>
                </Popover.Target>
                <Popover.Dropdown>
                    <ReactionPicker onPick={sendReaction} />
                </Popover.Dropdown>
            </Popover>
            <Tooltip label={<ShortcutHint keys={CALL_SHORTCUTS.hand} label={handLabel} />}>
                <ActionIcon
                    aria-label={handLabel}
                    aria-pressed={hand.raised}
                    color={hand.raised ? 'success' : undefined}
                    loading={hand.pending}
                    radius="var(--radius-rounded-full)"
                    size="lg"
                    variant={hand.raised ? 'filled' : 'default'}
                    visibleFrom="sm"
                    onClick={() => void hand.toggle()}
                >
                    <IconHandStop size={24} />
                </ActionIcon>
            </Tooltip>
            <RecordButton />
            <MoreMenu onOpenSettings={onOpenSettings} onOpenShortcuts={onOpenShortcuts}>
                <Menu.Item
                    color={hand.raised ? 'success' : undefined}
                    hiddenFrom="sm"
                    leftSection={<IconHandStop size={16} />}
                    onClick={() => void hand.toggle()}
                >
                    {handLabel}
                </Menu.Item>
                {browserSupportsScreenSharing && (
                    <Menu.Item
                        disabled={screenShare.pending}
                        hiddenFrom="sm"
                        leftSection={screenShare.enabled ? <IconScreenShareOff size={16} /> : <IconScreenShare size={16} />}
                        onClick={() => void screenShare.toggle()}
                    >
                        {screenShareLabel}
                    </Menu.Item>
                )}
                <Menu.Item
                    hiddenFrom="sm"
                    leftSection={tileLayout ? <IconLayoutSidebarRight size={16} /> : <IconLayoutGrid size={16} />}
                    onClick={() => {
                        setLayout(tileLayout ? 'speaker' : 'tile');
                    }}
                >
                    {layoutLabel}
                </Menu.Item>
                <Menu.Divider hiddenFrom="sm" />
            </MoreMenu>
            <Tooltip label={t('controls.leave')}>
                <ActionIcon
                    aria-label={t('controls.leave')}
                    color="danger"
                    radius="var(--radius-rounded-full)"
                    size="lg"
                    variant="filled"
                    w="var(--spacing-14)"
                    onClick={() => {
                        setLeaveOpened(true);
                    }}
                >
                    <IconPhone size={24} />
                </ActionIcon>
            </Tooltip>
            <LeaveDialog
                opened={leaveOpened || leaveGuard.state === 'blocked'}
                onClose={closeLeave}
                onLeave={() => {
                    closeLeave();
                    void room.disconnect();
                }}
            />
        </div>
    );
}
